"use client";

import { useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { handleSubmit } from "@/app/(application)/(normal)/isolate/components/input/handleSubmit";
import { HyperParameters } from "@/app/(application)/(normal)/isolate/components/input/HyperParameters";
import { Button } from "@/components/buttons/Button";
import { FileCoverInput } from "@/components/fileCoverInput/FileCoverInput";
import { Skeleton } from "@/components/skeletons/Skeleton";
import { Heading4 } from "@/components/text/Heading4";
import { SoftText } from "@/components/text/SoftText";
import { FilePresentation } from "@/components/uploadFilePresentation/FilePresentation";
import { UploadFilePresentation } from "@/components/uploadFilePresentation/UploadFilePresentation";
import { MINUTES_MAX_UPLOAD_ISOLATION } from "@/constants/constants";
import { isolationNamespace } from "@/constants/namespaces";
import { useAudioInput } from "@/lib/zustand/useAudioInput";
import { AudioInputState, UserNoDates } from "@/types/types";
import { getMaxUploadMinutesLimit } from "@/utils/getMaxUploadMinutesLimit";

export function Input({
  user,
  showCoverInput,
  shouldShowLoadState,
}: {
  user: UserNoDates | null;
  showCoverInput: boolean;
  shouldShowLoadState: boolean;
}) {
  const hasUser = user !== null;

  const [isolationId, setIsolationId] = useState<string>(uuidv4());
  const [removeInstrumental, setRemoveInstrumental] = useState(true);
  const [removeBackgroundVocals, setRemoveBackgroundVocals] = useState(false);
  const [deReverbDeEcho, setDeReverbDeEcho] = useState(false);
  const [deNoise, setDeNoise] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const useIsolationInput = useAudioInput((state) =>
    state.getNamedStore(isolationNamespace)
  );
  const selectedFileCover = useIsolationInput(
    (state) => state.selectedFileCover
  );
  const clearInput = useIsolationInput((state) => state.clearInput);
  const setInputCover = useIsolationInput((state) => state.setInputCover);
  const setInputFile = useIsolationInput((state) => state.setInputFile);

  const selectedFileCoverRef =
    useRef<AudioInputState["selectedFileCover"]>(selectedFileCover);

  // Update ref value whenever selectedFileCover changes.
  useEffect(() => {
    selectedFileCoverRef.current = selectedFileCover;
  }, [selectedFileCover]);

  const createIsolation = async () => {
    if (!hasUser) {
      throw new Error("No user found");
    }

    setSubmitting(true);
    setIsolationId(uuidv4());

    if (selectedFileCoverRef.current === null || selectedFileCover === null) {
      setSubmitting(false);
      throw new Error("No file selected");
    }

    try {
      await handleSubmit({
        isolationId,
        selectedFileCover,
        clearInput,
        user,
        removeInstrumental,
        removeBackgroundVocals,
        deReverbDeEcho,
        deNoise,
        selectedFileCoverRef,
      });
    } catch (error) {
      console.error(error);
      setSubmitting(false);
    }
    setSubmitting(false);
  };

  return (
    <div>
      <div className="w-full flex-1 rounded-3xl bg-gray-50 px-4 py-4 shadow-sm ring-1 ring-gray-900/5 sm:mx-0 sm:px-8 sm:py-8">
        <Heading4 elementType="h2">Input</Heading4>

        <SoftText elementType="p" className="mb-4 mt-1">
          Upload a song and choose the appropriate settings. This tool also uses
          your conversion time.
        </SoftText>

        {!shouldShowLoadState ?
          <FileCoverInput
            user={user}
            checkSubscription={true}
            audioContext={null}
            audioLengthMinLimitMinutes={null}
            audioLengthMaxLimitMinutes={getMaxUploadMinutesLimit(
              user,
              MINUTES_MAX_UPLOAD_ISOLATION
            )}
            showRecorder={false}
            adjustAcoustic={false}
            showSelectCoverText={false}
            showCover={showCoverInput}
            setInputCover={setInputCover}
            setInputFiles={[setInputFile]}
            presentation={
              <UploadFilePresentation
                selectedFileCover={selectedFileCover}
                clearInput={clearInput}
                hideClearButton={submitting}
                renderFilePresentation={(selectedFile) => (
                  <FilePresentation
                    selectedFile={selectedFile}
                    clearInput={clearInput}
                    hideClearButton={submitting}
                    footerSlot={null}
                  />
                )}
              />
            }
            currentAudioLengthS={0}
            multipleFiles={false}
            maxMultiAudioLengthMinutes={null}
            empty={selectedFileCover === null}
          />
        : <Skeleton className="h-48 w-full rounded-2xl" />}

        <HyperParameters
          removeInstrumental={removeInstrumental}
          setRemoveInstrumental={setRemoveInstrumental}
          removeBackgroundVocals={removeBackgroundVocals}
          setRemoveBackgroundVocals={setRemoveBackgroundVocals}
          deReverbDeEcho={deReverbDeEcho}
          setDeReverbDeEcho={setDeReverbDeEcho}
          deNoise={deNoise}
          setDeNoise={setDeNoise}
          shouldShowLoadState={shouldShowLoadState}
        />

        {selectedFileCover !== null &&
          hasUser &&
          (removeInstrumental ||
            removeBackgroundVocals ||
            deReverbDeEcho ||
            deNoise) && (
            <div className="mt-6 flex w-full items-center justify-between gap-x-10">
              <SoftText elementType="p" className="max-w-xs text-xs">
                By pressing 'Isolate', you confirm that this is your content and
                you have the rights to use it.
              </SoftText>
              <Button
                testId={`isolation-id-input-${isolationId}`}
                disabled={submitting}
                variant="primary"
                loading={submitting}
                onClick={createIsolation}
              >
                Isolate
              </Button>
            </div>
          )}
      </div>
    </div>
  );
}
